import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const WOOTRIC_SELECTOR =
  '[id*="wootric"], [class*="wootric"], [id*="WootricSurveyContainer"], #wootric-modal';

const COOKIE_CONSENT_SELECTOR = '#access-cookie-policies-container';

/**
 * Returns true when an interact-outside event originated from a Wootric
 * survey element.  Used by Sheet/Dialog to avoid dismissing overlays
 * when the user clicks the Wootric close button.
 */
export function isWootricInteraction(event: { detail?: { originalEvent?: Event } }): boolean {
  const target = event.detail?.originalEvent?.target;
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(WOOTRIC_SELECTOR);
}

/**
 * Returns true when an interact-outside event originated from the cookie
 * consent SDK container.  Used by Sheet/Dialog to keep overlays from
 * swallowing clicks on the consent banner.
 */
export function isCookieConsentInteraction(event: { detail?: { originalEvent?: Event } }): boolean {
  const target = event.detail?.originalEvent?.target;
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(COOKIE_CONSENT_SELECTOR);
}

// Format currency in GBP
export function formatCurrency(amount: number | undefined | null, showSymbol: boolean = true): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return showSymbol ? '£0.00' : '0.00';
  }
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  
  return showSymbol ? formatted : formatted.replace('£', '').trim();
}

// Format percentage
export function formatPercent(value: number | undefined | null, decimals: number = 1): string {
  if (value === undefined || value === null || isNaN(value)) {
    return `${(0).toFixed(decimals)}%`;
  }
  return `${value.toFixed(decimals)}%`;
}

// Format date in UK format (DD/MM/YYYY)
// Returns 'Not Set' for null, undefined, invalid dates, or Unix epoch (01/01/1970)
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return 'Not Set';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Not Set';
  // Check for Unix epoch (1970-01-01) which indicates NULL from database
  if (d.getFullYear() === 1970 && d.getMonth() === 0 && d.getDate() === 1) {
    return 'Not Set';
  }
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

// Format date for HTML date input (YYYY-MM-DD)
export function formatDateForInput(date: string | Date | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Format date with time
export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

// Format relative time (e.g., "2 hours ago")
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return formatDate(d);
}

// Get health color classes
export function getHealthColor(health: 'green' | 'amber' | 'red'): string {
  switch (health) {
    case 'green':
      return 'text-success-600 bg-success-50 border-success-200';
    case 'amber':
      return 'text-warning-600 bg-warning-50 border-warning-200';
    case 'red':
      return 'text-error-600 bg-error-50 border-error-200';
  }
}

// Map health value to Evo Design System Badge variant
export function healthToVariant(health: 'green' | 'amber' | 'red'): 'success' | 'warning' | 'destructive' {
  switch (health) {
    case 'green': return 'success';
    case 'amber': return 'warning';
    case 'red': return 'destructive';
  }
}

// Get status color
export function getStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-success-100 text-success-800';
    case 'in-progress':
      return 'bg-info-100 text-info-800';
    case 'not-started':
      return 'bg-neutral-100 text-neutral-800';
    case 'on-hold':
      return 'bg-warning-100 text-warning-800';
    case 'cancelled':
      return 'bg-error-100 text-error-800';
    default:
      return 'bg-neutral-100 text-neutral-800';
  }
}

// Get utilisation status color
export function getUtilisationColor(percent: number): string {
  if (percent < 70) return 'text-warning-600';
  if (percent > 100) return 'text-error-600';
  return 'text-success-600';
}

// Get conflict level color
export function getConflictColor(level: 'none' | 'warning' | 'critical'): string {
  switch (level) {
    case 'none':
      return 'text-success-600';
    case 'warning':
      return 'text-warning-600';
    case 'critical':
      return 'text-error-600';
  }
}

// Calculate variance color
export function getVarianceColor(variancePercent: number): string {
  if (variancePercent <= 0) return 'text-success-600';
  if (variancePercent < 10) return 'text-warning-600';
  return 'text-error-600';
}

// Format status label
export function formatStatus(status: string): string {
  return status
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Calculate days between dates
export function daysBetween(start: string | Date, end: string | Date): number {
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const endDate = typeof end === 'string' ? new Date(end) : end;
  const diffTime = endDate.getTime() - startDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Check if date is overdue
export function isOverdue(date: string | Date): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d < new Date();
}

// Truncate text
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
}

// Get initials from name
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

// Mask sensitive data
export function maskCurrency(amount: number): string {
  return '£' + '*'.repeat(formatCurrency(amount, false).length);
}

export function maskPercent(value: number): string {
  return '**.*%';
}

// Calculate days remaining until end date
// Returns null for null, undefined, invalid dates, or Unix epoch (01/01/1970)
export function calculateDaysRemaining(endDate: string | Date | null | undefined): number | null {
  if (!endDate) return null;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  if (isNaN(end.getTime())) return null;
  // Check for Unix epoch (1970-01-01) which indicates NULL from database
  if (end.getFullYear() === 1970 && end.getMonth() === 0 && end.getDate() === 1) {
    return null;
  }
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Get color for days remaining
export function getDaysRemainingColor(days: number): string {
  if (days < 0) return 'text-error-600'; // Overdue
  if (days < 7) return 'text-error-600'; // Less than a week
  if (days < 30) return 'text-warning-600'; // Less than a month
  return 'text-success-600'; // More than a month
}

// Format project type
export function formatProjectType(type: string): string {
  switch (type) {
    case 'fixed-price':
      return 'Fixed Price';
    case 'time-and-materials':
      return 'T&M';
    case 'retainer':
      return 'Retainer';
    default:
      return formatStatus(type);
  }
}

// Get project type color
export function getProjectTypeColor(type: string): string {
  switch (type) {
    case 'fixed-price':
      return 'bg-info-100 text-info-800';
    case 'time-and-materials':
      return 'bg-purple-100 text-purple-800';
    case 'retainer':
      return 'bg-cyan-100 text-cyan-800';
    default:
      return 'bg-neutral-100 text-neutral-800';
  }
}

// Calculate gross profit
export function calculateGrossProfit(actualRevenue: number, actualLabour: number, actualExpense: number): number {
  return actualRevenue - (actualLabour + actualExpense);
}

// ─────────────────────────────────────────────────────────────────────────────
// EAC (Estimate at Completion) — three-method implementation
// PRD: Project Forecast Report (EAC) — Section 6.1
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The three supported EAC calculation methods.
 *
 * - `simple`            — Actual Costs + Remaining Budget (assumes future spend follows plan)
 * - `performance-based` — Budget ÷ CPI (projects past cost performance into the future)
 * - `re-estimate`       — Actual Costs + PM's manually-entered Estimate to Complete
 */
export type EacMethod = 'simple' | 'performance-based' | 're-estimate';

/**
 * Inputs required for any EAC calculation.
 *
 * `totalBudget` MUST be `CostBudgetTotals.budget` — all cost categories, not
 * just labour + expense. Using `budgetLabour + budgetExpense` is a known bug
 * that this interface is designed to prevent.
 */
export interface EacInputs {
  /** Total actual costs from Finance (project.actualCosts) */
  actualCosts: number;
  /** CostBudgetTotals.budget — ALL cost categories, not just labour + expense */
  totalBudget: number;
  /** 0–100 */
  completionPercent: number;
  /** Only used for the 're-estimate' method — PM's estimate of remaining spend */
  estimateToComplete?: number;
}

/**
 * Full result returned by `calculateEAC()`.
 *
 * All monetary values are in GBP (same currency as inputs).
 */
export interface EacResult {
  /** Estimate at Completion — total projected final cost */
  eac: number;
  /** Budget × (completionPercent / 100) */
  earnedValue: number;
  /** Budget − Earned Value (for 'simple') or EAC − Actual Costs (for others) */
  remainingBudget: number;
  /** EAC − totalBudget. Positive = cost overrun; negative = under budget */
  costVarianceAtCompletion: number;
  /** Cost Performance Index — only present for 'performance-based' method */
  cpi?: number;
  /** The method that produced this result */
  method: EacMethod;
}

/**
 * calculateEAC
 *
 * Computes the Estimate at Completion using one of three methods.
 *
 * @param inputs  - Cost and completion data (see EacInputs)
 * @param method  - Which EAC method to apply
 * @returns       Full EacResult including EAC, earned value, variance, and optional CPI
 *
 * @example
 * // Simple method — project is 50% complete, £60k spent against £100k budget
 * calculateEAC({ actualCosts: 60000, totalBudget: 100000, completionPercent: 50 }, 'simple')
 * // → { eac: 110000, earnedValue: 50000, remainingBudget: 50000, costVarianceAtCompletion: 10000, method: 'simple' }
 */
export function calculateEAC(inputs: EacInputs, method: EacMethod): EacResult {
  const { actualCosts, totalBudget, completionPercent, estimateToComplete } = inputs;

  const earnedValue = totalBudget * (completionPercent / 100);

  switch (method) {
    case 'simple': {
      const remainingBudget = totalBudget - earnedValue;
      const eac = actualCosts + remainingBudget;
      return {
        eac,
        earnedValue,
        remainingBudget,
        costVarianceAtCompletion: eac - totalBudget,
        method,
      };
    }

    case 'performance-based': {
      // CPI = Earned Value ÷ Actual Costs
      // EAC  = Budget ÷ CPI  (assumes future spend mirrors past performance)
      // Guard against division by zero: if no actuals yet, CPI = 1 (on plan)
      const cpi = actualCosts > 0 ? earnedValue / actualCosts : 1;
      // Guard against zero CPI (should not occur with real data)
      const eac = cpi > 0 ? totalBudget / cpi : totalBudget;
      const remainingBudget = eac - actualCosts;
      return {
        eac,
        earnedValue,
        remainingBudget,
        costVarianceAtCompletion: eac - totalBudget,
        cpi,
        method,
      };
    }

    case 're-estimate': {
      // EAC = Actual Costs + PM's Estimate to Complete (session-only in Phase 1)
      const etc = estimateToComplete ?? 0;
      const eac = actualCosts + etc;
      const remainingBudget = etc;
      return {
        eac,
        earnedValue,
        remainingBudget,
        costVarianceAtCompletion: eac - totalBudget,
        method,
      };
    }

    default:
      throw new Error(`Unknown EAC method: ${method}`);
  }
}

/**
 * calculateForecastAtCompletion
 *
 * @deprecated Use `calculateEAC()` directly.
 *
 * Retained for backwards compatibility — callers in the Excel export and any
 * other legacy code continue to work unchanged. This wrapper delegates to
 * `calculateEAC()` using the Simple method.
 *
 * NOTE: This wrapper preserves the old (buggy) budget calculation —
 * `budgetLabour + budgetExpense` — so legacy callers are not silently changed.
 * To get the correct EAC, call `calculateEAC()` with `CostBudgetTotals.budget`
 * as the `totalBudget` directly.
 */
export function calculateForecastAtCompletion(
  actualLabour: number,
  actualExpense: number,
  budgetLabour: number,
  budgetExpense: number,
  completionPercent: number
): number {
  if (completionPercent === 0) return budgetLabour + budgetExpense;
  const result = calculateEAC(
    {
      actualCosts: actualLabour + actualExpense,
      totalBudget: budgetLabour + budgetExpense, // ← old callers pass these fields; see deprecation note
      completionPercent,
    },
    'simple',
  );
  return result.eac;
}

/**
 * migrateNotes — normalises legacy plain-text notes to HTML so they can be
 * safely rendered via dangerouslySetInnerHTML alongside Tiptap-produced HTML.
 *
 * Rules:
 *   - Empty / whitespace-only  → ''
 *   - Already starts with '<'  → return as-is (already HTML)
 *   - Plain text               → wrap each non-empty line in <p>…</p>
 */
export function migrateNotes(content: string | null | undefined): string {
  if (!content || !content.trim()) return '';
  const trimmed = content.trim();
  if (trimmed.startsWith('<')) return trimmed;
  // Plain text: split on newlines, wrap non-empty lines in <p>
  return trimmed
    .split('\n')
    .map(line => line.trim())
    .map(line => (line ? `<p>${line}</p>` : '<p></p>'))
    .join('');
}

// Get WIP amount color
export function getWIPColor(wipAmount: number): string {
  if (wipAmount < 0) return 'text-error-600'; // Over-billed
  if (wipAmount > 50000) return 'text-warning-600'; // High WIP
  return 'text-success-600'; // Normal
}

// ─────────────────────────────────────────────────────────────────────────────
// Task 6.5a — Invoice Template Appearance utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * getContrastTextColor
 * Returns '#ffffff' or '#1e293b' (near-black) depending on which gives better
 * WCAG contrast against the given background hex colour.
 *
 * Uses the relative luminance formula (WCAG 2.1):
 *   L = 0.2126 R + 0.7152 G + 0.0722 B  (each channel linearised)
 * White text is chosen when the background is dark (L < 0.179).
 *
 * Fallback: if the hex string is invalid, returns '#1e293b'.
 */
export function getContrastTextColor(hex: string): string {
  const clean = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return '#1e293b';

  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  // Linearise sRGB channels
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);

  return L < 0.179 ? '#ffffff' : '#1e293b';
}

/**
 * FONT_STACKS
 * Canonical map of font-family key → CSS font stack.
 * Keys are stored in template.branding.fontFamily.
 * Use `FONT_STACKS[key] ?? key` so older records that stored the raw CSS stack
 * still render correctly as a fallback.
 *
 * Task 6.6a
 */
export const FONT_STACKS: Record<string, string> = {
  // Sans-serif
  inter:          "Inter, system-ui, sans-serif",
  roboto:         "Roboto, Arial, sans-serif",
  helveticaNeue:  "'Helvetica Neue', Helvetica, Arial, sans-serif",
  openSans:       "'Open Sans', Arial, sans-serif",
  lato:           "Lato, Arial, sans-serif",
  montserrat:     "Montserrat, Arial, sans-serif",
  raleway:        "Raleway, Arial, sans-serif",
  nunito:         "Nunito, Arial, sans-serif",
  poppins:        "Poppins, Arial, sans-serif",
  sourceSans3:    "'Source Sans 3', Arial, sans-serif",
  // Serif
  georgia:        "Georgia, 'Times New Roman', serif",
  merriweather:   "Merriweather, Georgia, serif",
  playfairDisplay:"'Playfair Display', Georgia, serif",
  lora:           "Lora, Georgia, serif",
  cormorant:      "'Cormorant Garamond', Georgia, serif",
  // Monospace
  sourceCodePro:  "'Source Code Pro', 'Courier New', monospace",
  jetbrainsMono:  "'JetBrains Mono', 'Courier New', monospace",
};

/**
 * hexToRgba
 * Converts a 6-digit hex colour + alpha (0–1) into a CSS `rgba(…)` string.
 * Used for the 5 % accent-colour tint on alternate table rows.
 *
 * Falls back to `rgba(37,99,235,<alpha>)` (the default accent blue) when the
 * hex string is invalid.
 *
 * Task 6.7a
 */
export function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    return `rgba(37,99,235,${alpha})`; // fallback: #2563eb
  }
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Formats a Date as YYYY-MM-DD using the browser's local time, avoiding the
 * UTC timezone shift that `.toISOString()` introduces (which can move the date
 * back by one day for users in positive UTC offsets like BST).
 */
export function toLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
